import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Backdrop from "components/Backdrop";
import ErrorBox from "components/ErrorBox";
import { getAllPolicies, deletePolicy } from "api/apiHelper";
import { usePermissions } from "app/hooks";
import { useNavigate } from 'react-router-dom';
import IconButton from "components/IconButton";
import { toast } from "react-toastify";


type PolicyType = {
  id: number;
  title: string;
  description: string;
  document: string;
  uploaded_at: string;
};


const PolicyList = () => {
    const [policies, setPolicies] = useState<PolicyType[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [error, setError] = useState<string | null>(null)
    const permissions = usePermissions() || []
    const navigate = useNavigate()
    const [selectedPolicy, setSelectedPolicy] = useState<PolicyType | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
  
    const getPolicies = async () => {
      try {
        const response = await getAllPolicies()
        setPolicies(response.data)
        setIsLoading(false)
      } catch (err: any) {
        console.error(err)
        setError(err.message)
        setIsLoading(false)
      }
    }
  
    useEffect(() => {
      getPolicies()
    }, [])
  
    const formatDate = (dateString: string) => {
      const options: Intl.DateTimeFormatOptions = { year: "numeric", month: "long", day: "numeric" }
      return new Date(dateString).toLocaleDateString(undefined, options)
    }
  
    return (
      <div className="container mx-auto px-4 py-8">
        {isLoading ? (
          <Backdrop show={isLoading} />
        ) : error ? (
          <ErrorBox errorMessage={error} />
        ) : (
          <div>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-800 dark:text-white">Company Policies</h2>
                <p className="text-gray-600 dark:text-gray-300 mt-2">
                  Access and review all company policies and guidelines
                </p>
              </div>
              {permissions && permissions.includes('policy.add_policy') &&
                <IconButton url="/policy/create" name="Create Policy" icon="fas fa-plus" extraStyles="mb-2 md:mb-0" />
              }
            </div>
  
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {policies.map((policy) => (
                <div
                  key={policy.id}
                  className="bg-white dark:bg-navy-800 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 border border-gray-100 dark:border-navy-700 h-full flex flex-col"
                >
                  <div className="p-5 bg-gray-50 dark:bg-navy-900 border-b border-gray-100 dark:border-navy-700">
                    <div className="flex items-center">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 text-brand-500 dark:text-blue-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-white truncate">{policy.title}</h3>
                    </div>
                  </div>
  
                  <div className="p-5 flex-grow">
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-3">{policy.description}</p>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                      <span className="flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        Created: {formatDate(policy.uploaded_at)}
                      </span>
                    </div>
                  </div>
  
                  <div className="p-5 pt-0 mt-auto">
                    <div className="flex justify-between items-center">
                      <a
                        href={policy.document}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-brand-500 hover:text-brand-600 dark:text-blue-400 dark:hover:text-blue-500 font-medium transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                        View Policy
                      </a>
                      {permissions && permissions.includes('policy.add_policy') &&
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPolicy(policy);
                                setShowDeleteModal(true);
                            }}
                            className="inline-flex items-center text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium transition-colors"
                            >
                            <svg xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 mr-1"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            > <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                /></svg> Delete
                        </button>
                        }  
                    </div>
                  </div>
                </div>
              ))}
            </div>
              {showDeleteModal && selectedPolicy && (
                <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
                    <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md text-center">
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">
                        Confirm Delete
                    </h2>
                    <p className="text-gray-600 mb-4">
                        Are you sure you want to delete <strong>{selectedPolicy.title}</strong>?
                    </p>
                    <div className="flex justify-center gap-4">
                        <button
                        onClick={async () => {
                            try {
                            await deletePolicy(selectedPolicy.id);
                            toast.success("Policy deleted successfully");
                            setShowDeleteModal(false);
                            setSelectedPolicy(null);
                            getPolicies();
                            } catch {
                            toast.error("Failed to delete policy");
                            }
                        }}
                        className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
                        >
                        Delete
                        </button>
                        <button
                        onClick={() => {
                            setShowDeleteModal(false);
                            setSelectedPolicy(null);
                        }}
                        className="bg-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300"
                        >
                        Cancel
                        </button>
                    </div>
                </div>
            </div>
            )}
            
  
            {policies.length === 0 && (
              <div className="text-center py-12 bg-gray-50 dark:bg-navy-900 rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12 mx-auto text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No policies found</h3>
                <p className="mt-1 text-gray-500 dark:text-gray-400">Get started by creating your first policy.</p>
                {permissions && permissions.includes('policy.add_policy') &&
                    <button
                    className="mt-6 px-4 py-2 bg-brand-500 text-white rounded-md dark:bg-brand-400 dark:hover:bg-brand-500 hover:bg-brand-700"
                    onClick={() => navigate("/policy/create")}
                    >
                    Create Policy
                    </button>
                }
              </div>
            )}
          </div>
        )}
      </div>
    )
  }
  
  export default PolicyList
  
